// eslint-disable-next-line no-unused-vars
import { EventStageTypes } from "common/constants/EventStageTypes";
// eslint-disable-next-line no-unused-vars
import { EventFormat } from "../types/EventFormat";
// eslint-disable-next-line no-unused-vars
import { FreeformBracketPart, Match } from "bracket-system/types";
import { v4 as uuidv4 } from "uuid";

export default class EventFormatHelper {
    /**
     * Gets the format builder for the provided format.
     *
     * @param {EventFormat} eventFormat
     * @returns {EventFormatBuilder} The builder object.
     */
    static getEventFormatBuilder(eventFormat) {
        return new EventFormatBuilder(eventFormat);
    }
}

export class EventFormatBuilder {
    /** @type {EventFormat} */
    eventFormat;

    /** @type {EventFormatData} */
    data;

    /**
     *
     * @param {EventFormat} eventFormat
     */
    constructor(eventFormat) {
        this.eventFormat = eventFormat ?? {};

        // Convert from strings to dates, and parse json to object.
        this.eventFormat.createdDate = this.eventFormat.created
            ? new Date(this.eventFormat.created)
            : null;
        this.eventFormat.modifiedDate = this.eventFormat.modified
            ? new Date(this.eventFormat.modified)
            : null;
        this.data = this.eventFormat?.json
            ? JSON.parse(this.eventFormat.json)
            : {
                  stages: [
                      {
                          name: "Group Stage",
                          type: EventStageTypes.Group,
                          groupStageDetails: new EventFormatGroupStageDetails(),
                      },
                      {
                          name: "Playoffs",
                          type: EventStageTypes.Playoffs,
                      },
                  ],
              };

        buildScoringDetails(this);
    }
}

export class EventFormatData {
    /** @type {EventFormatStage[]} */
    stages;

    /** @type {EventFormatScoringBreakdown} */
    scoringBreakdown;
}

export class EventFormatScoringBreakdown {
    /** @type {EventFormatScoringBreakdownGroup} */
    groups = [];
}

export class EventFormatScoringBreakdownGroup {
    /** @type {string} */
    name;

    /** @type {EventFormatScoringBreakdownRecord[]} */
    records = [];
}

export class EventFormatScoringBreakdownRecord {
    /** @type {string} */
    name;

    /** @type {number|string} */
    pointsPer;

    /** Upper bound of correct picks for this row. @type {number} */
    maxCount;

    /** @type {string} */
    dbColumnName;

    /** Overrides the default "(+N per correct pick)" subtitle. @type {string} */
    subtitle;
}

export class EventFormatStage {
    /**
     * @type {string}
     */
    name;

    /**
     * What type of stage this is.
     * Playoffs, Swiss, Group.
     * @type {number}
     */
    type = EventStageTypes.Playoffs;

    /**
     * What are the group stage details.
     * @type {EventFormatGroupStageDetails}
     */
    groupStageDetails;

    /**
     * What are the swiss stage details.
     * @type {EventFormatSwissStageDetails}
     */
    swissStageDetails;

    /**
     * What are the playoff details.
     * @type {EventFormatPlayoffsDetails}
     */
    playoffDetails;

    /**
     * What are the bounty stage details.
     * @type {EventFormatBountyStageDetails}
     */
    bountyStageDetails;

    /**
     * # of teams to open picks
     * @type {number}
     */
    teamsToOpenPicks;

    /**
     * What is the bracket data?
     * Applies in playoffs only.
     * @type {EventFormatBracket}
     */
    bracket;
}

export class EventFormatGroupStageDetails {
    /**
     * If true, then we are using brackets for group stage.
     * If false, then we are using round robin for group stage.
     * @type {boolean}
     */
    hasGroupBrackets = false;

    /** @type {boolean} */
    useSeriesScore = false;

    /** @type {number} */
    groupCount = 4;

    /** @type {number} */
    advancingCount = 3;

    /** @type {number} */
    totalAdvancing = 3;

    /** @type {number} */
    pointsPerAdvancing = 2;

    /** @type {boolean} */
    scoreFirstPlace = true;

    /** @type {number} */
    pointsPerFirstPlace = 1;

    /** @type {boolean} */
    scoreSecondPlace = true;

    /** @type {number} */
    pointsPerSecondPlace = 1;
}

export class EventFormatSwissStageDetails {
    /** @type {number} */
    advancingCount = 8;

    /** @type {number} */
    pointsPerAdvancing = 3;

    /** @type {number} */
    cleanSweepWinnerCount = 2;

    /** @type {number} */
    cleanSweepLoserCount = 2;

    /** @type {number} */
    pointsPerCorrectWinner = 1;

    /** @type {number} */
    pointsPerCorrectLoser = 1;
}

export class EventFormatPlayoffsDetails {
    /** @type {boolean} */
    useSeriesScore = true;

    /** @type {boolean} */
    hasBracketReset = false;

    /** @type {number} */
    pointsForCorrectBracketReset = 1;
}

export class EventFormatBountyStageDetails {
    /** @type {number} */
    teamCount = 8;

    /** @type {number[]} */
    pointsPerCorrectPairing = [0, 2, 2];

    /** @type {number[]} */
    pointsPerCorrectSurvivor = [6, 8, 12];

    /** @type {boolean} */
    useSeriesScore = true;

    /** @type {number[]} */
    bestOfPerRound = [3, 3, 5];

    /** @type {string[]} */
    survivorRoundNames = ["Top 4", "Top 2", "Champions"];

    /** @type {string[]} */
    pairingRoundNames = [
        "Quarterfinal Matchups",
        "Semifinal Matchups",
        "Final Matchup",
    ];
}

/** A bracket instance within a stage. */
export class EventFormatBracket {
    /** @type {EventFormatBracketView[]} */
    views = [];

    /** @type {EventFormatBracketPart[]} */
    parts = [];

    /** @type {Array<{upper: Match[], lower: Match[]}>} */
    matches;

    /** @type {Array<FreeformBracketPart>} */
    freeformParts;

    /** @type {EventFormatBracketScoringGroup[]} */
    scoringGroups = [];

    /** @type {boolean} */
    freeform = false;
}

export class EventFormatBracketView {
    /** @type {string} */
    name;

    /** @type {EventFormatBracketColumnMatchTarget[]} */
    matches;

    /**
     *
     * @param {string} name
     * @param {boolean} showSeriesScore
     * @param {EventFormatBracketColumnMatchTarget[]} matches
     */
    constructor(name, showSeriesScore, matches) {
        this.name = name;
        this.showSeriesScore = showSeriesScore;
        this.matches = matches;
    }
}

/** A portion of the bracket. */
export class EventFormatBracketPart {
    /** @type {string} */
    identifier;
    /** @type {number} */
    columnCount;
    /** @type {EventFormatBracketColumn[]} */
    columns;
}

export class EventFormatBracketColumn {
    /** @type {string} */
    name;
    /** @type {number} */
    index;
    /** @type {number} */
    matchCount;
    /** @type {EventFormatBracketColumnMatch[]} */
    matches;
    /** @type {number} */
    bestOf = 5;
    /** @type {boolean} */
    lockPicks;
    /** @type {boolean} */
    bothTeamsAdvance;
}

export class EventFormatBracketColumnMatch {
    /**
     * Set the bracket part this match is in.
     * @type {string}
     */
    part;

    /**
     * Set the column this match is in.
     * @type {number}
     */
    col;

    /**
     * Set the index within this column.
     * @type {number}
     */
    colIndex;

    /**
     * Where the loser will go (if applicable)
     * @type {EventFormatBracketColumnMatchTarget}
     */
    loserPath;

    /**
     * @type {string}
     */
    bracketMatchId;
}

export class EventFormatBracketColumnTarget {
    /**
     * Set the bracket part this match is in.
     * @type {string}
     */
    part;

    /**
     * The bracket column we are in.
     * @type {number}
     */
    col;
}

export class EventFormatBracketColumnMatchTarget extends EventFormatBracketColumnTarget {
    /**
     * The index within the column we are at.
     * @type {number}
     */
    colIndex;

    /**
     * The slot the team drops into.
     * @type {boolean | undefined | null}
     */
    isTop = true;
}

export class EventFormatBracketScoringGroup {
    /**
     * Set the name of this scoring group.
     * @type {string}
     */
    name;

    /**
     * Set the columns included in this scoring group
     * @type {EventFormatBracketColumnTarget[]} */
    columns = [];

    /** @type {boolean} */
    scoreParticipants = true;

    /** @type {boolean} */
    scoreWinnersOnly = false;

    /** @type {number} */
    pointsPerCorrectParticipant = 1;

    /** @type {boolean} */
    scorePlacement = true;

    /** @type {number} */
    pointsPerCorrectPlacement = 1;

    constructor(name) {
        this.name = name;
    }
}

/**
 *
 * @param {EventFormatBracket} bracket
 * @param {EventFormatBracketColumnMatchTarget[]} viewTargets
 * @returns {{upper: string[], lower: string[]}}
 */
export function getHeaderNamesFromBracket(bracket, viewTargets) {
    const headerNames = {
        upper: [],
        lower: [],
    };

    if (!bracket) {
        return headerNames;
    }

    for (const part of bracket.parts) {
        if (part.identifier === "UB") {
            headerNames.upper = part.columns
                .filter((c) =>
                    viewTargets
                        ? viewTargets.some(
                              (t) => t.part === "UB" && t.col === c.index
                          )
                        : true
                )
                .map((c) => c.name);
        } else if (part.identifier === "LB") {
            headerNames.lower = part.columns
                .filter((c) =>
                    viewTargets
                        ? viewTargets.some(
                              (t) => t.part === "LB" && t.col === c.index
                          )
                        : true
                )
                .map((c) => c.name);
        } else if (part.identifier === "GF") {
            headerNames.upper = headerNames.upper.concat(
                part.columns
                    .filter((c) =>
                        viewTargets
                            ? viewTargets.some((t) => t.part === "GF")
                            : true
                    )
                    .map((c) => c.name)
            );
        }
    }

    return headerNames;
}

/**
 *
 * @param {EventFormatBracket} bracket
 * @param {EventFormatBracketColumnMatchTarget[]} viewTargets
 * @returns {{upper: number[], lower: number[]}}
 */
export function getLockedColumns(bracket, viewTargets) {
    const lockedColumns = {
        upper: [],
        lower: [],
    };

    if (!bracket) {
        return lockedColumns;
    }

    for (const part of bracket.parts) {
        if (part.identifier === "UB") {
            lockedColumns.upper = part.columns
                .filter((c) => c.lockPicks)
                .map((c) => c.index);
        } else if (part.identifier === "LB") {
            lockedColumns.lower = part.columns
                .filter((c) => c.lockPicks)
                .map((c) => c.index);
        } else if (part.identifier === "GF") {
            lockedColumns.upper = lockedColumns.upper.concat(
                part.columns
                    .filter((c) =>
                        viewTargets
                            ? viewTargets.some((t) => t.part === "GF")
                            : true
                    )
                    .map((c) => c.name)
            );
        }
    }

    return lockedColumns;
}

/**
 *
 * @param {EventFormatStage} currentStage
 */
export function stageUsesBracket(currentStage) {
    return (
        (currentStage.type === EventStageTypes.Group &&
            currentStage.groupStageDetails.hasGroupBrackets) ||
        currentStage.type === EventStageTypes.Playoffs ||
        currentStage.type === EventStageTypes.Bounty
    );
}

/**
 * @param {number} teamCount
 * @returns {number}
 */
export function bountyRoundCount(teamCount) {
    return Math.round(Math.log2(teamCount ?? 8));
}

/**
 * A bad entry becomes `invalid` instead of dropping out, so indices don't shift.
 *
 * @param {string} text
 * @param {number} invalid
 * @returns {number[]}
 */
export function parsePerRoundList(text, invalid = 0) {
    return text.split(",").map((v) => {
        const n = Number(v.trim());
        return isNaN(n) || n < 0 ? invalid : n;
    });
}

/**
 * Score breakdown row names per round: the survivors by tier, the matchups by round.
 *
 * @param {number} teamCount
 * @returns {{ survivor: string, pairing: string }[]}
 */
export function bountyRoundScoreNames(teamCount) {
    const count = teamCount ?? 8;
    return Array.from({ length: bountyRoundCount(count) }, (_, round) => {
        const matchCount = count / Math.pow(2, round + 1);
        return {
            survivor: matchCount === 1 ? "Champions" : `Top ${matchCount}`,
            pairing: bountyRoundLabels(matchCount).matchups,
        };
    });
}

/**
 * Pads/truncates the per-round lists to the round count.
 *
 * @param {EventFormatBountyStageDetails} bountyStageDetails
 * @returns {EventFormatBountyStageDetails}
 */
export function normalizeBountyStageDetails(bountyStageDetails) {
    const roundCount = bountyRoundCount(bountyStageDetails?.teamCount);
    const toLength = (values, pad) =>
        Array.from({ length: roundCount }, (_, i) =>
            Array.isArray(values) && typeof values[i] === "number"
                ? values[i]
                : pad
        );
    const roundNames = bountyRoundScoreNames(bountyStageDetails?.teamCount);

    return {
        ...bountyStageDetails,
        pointsPerCorrectPairing: toLength(
            bountyStageDetails?.pointsPerCorrectPairing,
            0
        ),
        pointsPerCorrectSurvivor: toLength(
            bountyStageDetails?.pointsPerCorrectSurvivor,
            0
        ),
        bestOfPerRound: toLength(bountyStageDetails?.bestOfPerRound, 3),
        survivorRoundNames: roundNames.map((n) => n.survivor),
        pairingRoundNames: roundNames.map((n) => n.pairing),
    };
}

/**
 * Generates the bounty bracket: one single-column part per round, no links,
 * so rounds render as separate brackets instead of a connected tree.
 *
 * @param {EventFormatBountyStageDetails} bountyStageDetails
 * @returns {EventFormatBracket}
 */
export function generateBountyBracket(bountyStageDetails) {
    const teamCount = bountyStageDetails?.teamCount ?? 8;
    const roundCount = bountyRoundCount(teamCount);
    const bestOfPerRound = bountyStageDetails?.bestOfPerRound ?? [];

    const bracket = new EventFormatBracket();
    bracket.freeform = true;
    bracket.freeformParts = [];

    for (let round = 0; round < roundCount; round++) {
        const matchCount = teamCount / Math.pow(2, round + 1);
        const identifier = `${round}`;
        const name = bountyRoundLabels(matchCount).bracket;

        const matches = [];
        for (let i = 0; i < matchCount; i++) {
            const id = uuidv4();
            matches.push({
                paddingTop: 0,
                id,
                bracketMatchId: id,
                part: identifier,
                col: 0,
                colIndex: i,
                loserPath: null,
                nextMatchId: null,
                nextLooserMatchId: null,
                state: "PLAYED",
                participants: [],
                startTime: "",
            });
        }

        bracket.freeformParts.push({
            identifier,
            name,
            columnCount: 1,
            columns: [
                {
                    paddingTop: 0,
                    paddingLeft: 0,
                    part: identifier,
                    index: 0,
                    matches,
                    matchCount,
                    name,
                    bestOf: bestOfPerRound[round] ?? 3,
                    isLocked: false,
                    isQualifier: false,
                },
            ],
        });
    }

    return bracket;
}

/**
 * Bracket section header and matchup score row label for a round of `matchCount` matches.
 *
 * @param {number} matchCount
 * @returns {{ bracket: string, matchups: string }}
 */
function bountyRoundLabels(matchCount) {
    switch (matchCount) {
        case 1:
            return { bracket: "Grand Final", matchups: "Final Matchup" };
        case 2:
            return { bracket: "Semifinals", matchups: "Semifinal Matchups" };
        case 4:
            return { bracket: "Quarterfinals", matchups: "Quarterfinal Matchups" };
        default:
            return {
                bracket: `Round of ${matchCount * 2}`,
                matchups: `Round of ${matchCount * 2} Matchups`,
            };
    }
}

/**
 *
 * @param {EventFormatBuilder} currentFormat
 */
export function buildScoringDetails(currentFormat) {
    const scoringBreakdown = new EventFormatScoringBreakdown();
    for (const stage of currentFormat?.data?.stages) {
        const scoringGroup = buildScoringGroup(stage);
        scoringBreakdown.groups.push(scoringGroup);
    }

    currentFormat.data.scoringBreakdown = scoringBreakdown;

    const championPick = currentFormat?.data?.championPick;
    if (championPick?.enabled) {
        if (championPick.pointsAuto) {
            championPick.points =
                suggestedChampionPickPoints(currentFormat.data) ??
                championPick.points ??
                25;
        }

        scoringBreakdown.groups.push(
            buildChampionPickScoringGroup(championPick)
        );
    }
}

/**
 * @param {EventFormatData} data
 * @returns {number}
 */
export function maxPointsForFormat(data) {
    return sumMaxPoints(data?.scoringBreakdown?.groups);
}

/**
 * @param {EventFormatData} data
 * @returns {number|null} null while the format has nothing scorable yet
 */
export function suggestedChampionPickPoints(data) {
    const max = sumMaxPoints(
        (data?.scoringBreakdown?.groups ?? []).filter(
            (group) => group.name !== "Going All The Way"
        )
    );
    if (max <= 0) {
        return null;
    }

    return Math.max(5, Math.round((max * 0.125) / 5) * 5);
}

function sumMaxPoints(groups) {
    return (groups ?? [])
        .flatMap((group) => group.records ?? [])
        .reduce(
            (total, record) =>
                total + num(record.pointsPer) * num(record.maxCount),
            0
        );
}

/** Numeric format field that may arrive as a string. */
function num(value) {
    const n = Number(value);
    return isNaN(n) ? 0 : n;
}

/**
 * Bracket part options for the champion final.
 *
 * @param {EventFormatStage} stage
 * @returns {{ identifier: string, colCount: number }[]}
 */
export function championPickPartOptions(stage) {
    const keepsBracket =
        stage?.type === EventStageTypes.Playoffs ||
        stage?.type === EventStageTypes.Bounty ||
        (stage?.type === EventStageTypes.Group &&
            stage?.groupStageDetails?.hasGroupBrackets);
    if (!keepsBracket) {
        return [];
    }

    const parts = stage?.bracket?.freeformParts?.length
        ? stage.bracket.freeformParts
        : stage?.bracket?.parts;

    return (parts ?? [])
        .filter((part) => part?.identifier !== undefined)
        .map((part) => ({
            identifier: `${part.identifier}`,
            colCount: part.columns?.length ?? 0,
        }));
}

/**
 * True when the championPick coordinates point at a real stage/part/column.
 *
 * @param {EventFormatData} data
 */
export function championPickCoordinatesResolve(data) {
    const championPick = data?.championPick;
    const stage = data?.stages?.[championPick?.finalStageIndex];
    const part = championPickPartOptions(stage).find(
        (p) => p.identifier === championPick?.finalPart
    );

    return (
        !!part &&
        championPick.finalCol >= 0 &&
        championPick.finalCol < part.colCount
    );
}

/**
 * Default champion coordinates: last stage, GF part if present, last column.
 *
 * @param {EventFormatData} data
 */
export function defaultChampionPickCoordinates(data) {
    const finalStageIndex = (data?.stages?.length ?? 0) - 1;
    const parts = championPickPartOptions(data?.stages?.[finalStageIndex]);
    const finalPart =
        parts.find((p) => p.identifier === "GF") ?? parts[parts.length - 1];

    return {
        finalStageIndex,
        finalPart: finalPart?.identifier ?? "",
        finalCol: Math.max(0, (finalPart?.colCount ?? 1) - 1),
    };
}

/**
 * Name must match what usp_ScoreEvent writes into the score breakdown.
 *
 * @returns {EventFormatScoringBreakdownGroup}
 */
function buildChampionPickScoringGroup(championPick) {
    const scoringGroup = new EventFormatScoringBreakdownGroup();
    scoringGroup.name = "Going All The Way";

    const record = new EventFormatScoringBreakdownRecord();
    record.name = "Going All The Way";
    record.pointsPer = championPick.points;
    record.maxCount = 1;
    record.subtitle = `(+${championPick.points} one-time bonus)`;
    scoringGroup.records.push(record);

    return scoringGroup;
}

/**
 *
 * @param {EventFormatStage} stage
 * @returns {EventFormatScoringBreakdownGroup}
 */
function buildScoringGroup(stage) {
    const scoringGroup = new EventFormatScoringBreakdownGroup();

    switch (stage.type) {
        case EventStageTypes.Group:
            buildGroupStageScoringGroup(stage, scoringGroup);
            break;
        case EventStageTypes.Swiss:
            buildSwissStageScoringGroup(stage, scoringGroup);
            break;
        case EventStageTypes.Playoffs:
            buildPlayoffsScoringGroup(stage, scoringGroup);
            break;
        case EventStageTypes.Bounty:
            buildBountyScoringGroup(stage, scoringGroup);
            break;
        default:
            break;
    }

    return scoringGroup;
}

/**
 *
 * @param {EventFormatStage} stage
 */
function buildGroupStageScoringGroup(stage, scoringGroup) {
    const details = stage.groupStageDetails;
    if (!details) {
        return;
    }

    const groupCount = num(details.groupCount) || 1;
    // usp_ScoreEvent skips all three group rows when any of these four is null.
    const scorerActive = [
        details.advancingCount ?? details.totalAdvancing,
        details.pointsPerAdvancing,
        details.scoreFirstPlace,
        details.pointsPerFirstPlace,
    ].every((value) => value != null);

    if (details.hasGroupBrackets) {
        buildPlayoffsScoringGroup(stage, scoringGroup);
        // usp_ScoreEvent scores the rows below even for bracket group stages.
        if (!scorerActive) {
            return;
        }
    } else {
        scoringGroup.name = stage.name;
    }

    // Advancing
    const advancingToPlayoffs = new EventFormatScoringBreakdownRecord();
    advancingToPlayoffs.name = "Advancing to Next Stage";
    advancingToPlayoffs.pointsPer = details.pointsPerAdvancing;
    // totalAdvancing spans the stage, advancingCount is per group.
    advancingToPlayoffs.maxCount = !scorerActive
        ? 0
        : num(details.totalAdvancing ?? num(details.advancingCount) * groupCount);
    scoringGroup.records.push(advancingToPlayoffs);

    if (details.scoreFirstPlace) {
        const firstPlace = new EventFormatScoringBreakdownRecord();
        firstPlace.name = "1st Place Bonus";
        firstPlace.pointsPer = details.pointsPerFirstPlace;
        firstPlace.maxCount = scorerActive ? groupCount : 0;
        scoringGroup.records.push(firstPlace);

        if (details.scoreSecondPlace) {
            const secondPlace = new EventFormatScoringBreakdownRecord();
            secondPlace.name = "2nd Place Bonus";
            secondPlace.pointsPer = details.pointsPerSecondPlace;
            secondPlace.maxCount = scorerActive ? groupCount : 0;
            scoringGroup.records.push(secondPlace);
        }
    }
}

/**
 *
 * @param {EventFormatStage} stage
 */
function buildSwissStageScoringGroup(stage, scoringGroup) {
    if (stage.swissStageDetails) {
        scoringGroup.name = stage.name;

        const advancingToPlayoffs = new EventFormatScoringBreakdownRecord();
        advancingToPlayoffs.name = "Advancing to Next Stage";
        advancingToPlayoffs.pointsPer =
            stage.swissStageDetails.pointsPerAdvancing;
        advancingToPlayoffs.maxCount = num(
            stage.swissStageDetails.advancingCount
        );
        scoringGroup.records.push(advancingToPlayoffs);

        if (stage.swissStageDetails.pointsPerCorrectWinner > 0) {
            const cleanSweepWinner = new EventFormatScoringBreakdownRecord();
            cleanSweepWinner.name = "Clean Sweep Bonus (3-0)";
            cleanSweepWinner.pointsPer =
                stage.swissStageDetails.pointsPerCorrectWinner;
            cleanSweepWinner.maxCount = num(
                stage.swissStageDetails.cleanSweepWinnerCount
            );
            scoringGroup.records.push(cleanSweepWinner);
        }

        if (stage.swissStageDetails.pointsPerCorrectLoser > 0) {
            const cleanSweepLoser = new EventFormatScoringBreakdownRecord();
            cleanSweepLoser.name = "Clean Sweep Bonus (0-3)";
            cleanSweepLoser.pointsPer =
                stage.swissStageDetails.pointsPerCorrectLoser;
            cleanSweepLoser.maxCount = num(
                stage.swissStageDetails.cleanSweepLoserCount
            );
            scoringGroup.records.push(cleanSweepLoser);
        }
    }
}

/**
 * One row per round, so a round's weight and its score line up. Names must match what
 * usp_ScoreEvent writes into the score breakdown.
 *
 * @param {EventFormatStage} stage
 */
function buildBountyScoringGroup(stage, scoringGroup) {
    if (!stage.bountyStageDetails) {
        return;
    }

    scoringGroup.name = stage.name;

    const teamCount = num(stage.bountyStageDetails.teamCount) || 8;
    const roundNames = bountyRoundScoreNames(teamCount);
    // A zero-weight round earns nothing, so it gets no row (round 1 never scores matchups).
    const addRounds = (points, nameOf) => {
        roundNames.forEach((names, round) => {
            const pointsPer = points?.[round] ?? 0;
            if (pointsPer <= 0) {
                return;
            }

            const record = new EventFormatScoringBreakdownRecord();
            record.name = nameOf(names);
            record.pointsPer = pointsPer;
            record.maxCount = teamCount / Math.pow(2, round + 1);
            scoringGroup.records.push(record);
        });
    };

    addRounds(
        stage.bountyStageDetails.pointsPerCorrectSurvivor,
        (n) => n.survivor
    );
    addRounds(
        stage.bountyStageDetails.pointsPerCorrectPairing,
        (n) => n.pairing
    );

    if (stage.bountyStageDetails.useSeriesScore) {
        const seriesBonus = new EventFormatScoringBreakdownRecord();
        seriesBonus.name = "Series Score Bonus";
        seriesBonus.pointsPer = 1;
        // A knockout of N teams plays N-1 matches.
        seriesBonus.maxCount = teamCount - 1;
        scoringGroup.records.push(seriesBonus);
    }
}

/**
 *
 * @param {EventFormatStage} stage
 */
function buildPlayoffsScoringGroup(stage, scoringGroup) {
    scoringGroup.name = stage.name;
    if (!stage?.bracket?.scoringGroups) {
        return;
    }

    // A group stage scores its bracket once per group.
    const groupCount =
        stage.type === EventStageTypes.Group
            ? num(stage.groupStageDetails?.groupCount) || 1
            : 1;

    for (const bracketGroup of stage?.bracket?.scoringGroups) {
        if (bracketGroup.scoreParticipants) {
            let breakdownRecord = new EventFormatScoringBreakdownRecord();
            breakdownRecord.name = bracketGroup.name;
            breakdownRecord.pointsPer =
                bracketGroup.pointsPerCorrectParticipant;
            breakdownRecord.maxCount =
                groupCount *
                distinctBracketSlots(
                    stage.bracket,
                    bracketGroup.columns,
                    bracketGroup.scoreWinnersOnly
                );
            scoringGroup.records.push(breakdownRecord);
        }
    }

    const placementGroup = stage.bracket?.scoringGroups?.find(
        (g) => g.scorePlacement
    );

    if (placementGroup) {
        let placementBonus = new EventFormatScoringBreakdownRecord();
        placementBonus.name = "Placement Bonus";
        placementBonus.pointsPer = 1;
        placementBonus.maxCount =
            groupCount *
            distinctBracketSlots(
                stage.bracket,
                stage.bracket.scoringGroups
                    .filter((g) => g.scorePlacement)
                    .flatMap((g) => g.columns ?? []),
                placementGroup.scoreWinnersOnly
            );
        scoringGroup.records.push(placementBonus);
    }

    const hasSeries =
        stage.groupStageDetails?.useSeriesScore ||
        stage.playoffDetails?.useSeriesScore;
    if (hasSeries) {
        let seriesBonus = new EventFormatScoringBreakdownRecord();
        seriesBonus.name = "Series Score Bonus";
        seriesBonus.pointsPer = 1;
        seriesBonus.maxCount = groupCount * seriesMatchCount(stage.bracket);
        scoringGroup.records.push(seriesBonus);
    }

    // const hasBracketReset = stage.playoffDetails?.hasBracketReset;
    // if (hasBracketReset) {
    // 	let seriesBonus = new EventFormatScoringBreakdownRecord();
    // 	seriesBonus.name = "Bracket Reset Bonus";
    // 	seriesBonus.pointsPer = 1;
    // 	scoringGroup.records.push(seriesBonus);
    // }
}

/**
 * Scorable team slots in the targeted columns. A team advancing between
 * two selected columns counts once, as in the scoring procs.
 *
 * @param {EventFormatBracket} bracket
 * @param {EventFormatBracketColumnTarget[]} targets
 * @param {boolean} scoreWinnersOnly
 * @returns {number}
 */
function distinctBracketSlots(bracket, targets, scoreWinnersOnly) {
    const parts =
        (bracket?.freeform ? bracket?.freeformParts : bracket?.parts) ?? [];
    const matches = (targets ?? []).flatMap(
        (target) =>
            parts
                .find((part) => `${part.identifier}` === `${target.part}`)
                ?.columns?.find(
                    (column) => num(column.index) === num(target.col)
                )?.matches ?? []
    );
    const ids = new Set(matches.map((match) => match.id));

    if (scoreWinnersOnly) {
        const fedTwice = matches.filter(
            (match) =>
                matches.filter((feeder) => feeder.nextMatchId === match.id)
                    .length === 2
        ).length;
        return matches.length - fedTwice;
    }

    const sharedTeams =
        matches.filter((match) => ids.has(match.nextMatchId)).length +
        matches.filter((match) => ids.has(match.nextLooserMatchId)).length;
    return matches.length * 2 - sharedTeams;
}

/** Matches the series bonus can score. usp_ScoreSeriesScore skips lockPicks columns. */
function seriesMatchCount(bracket) {
    const locked = new Set();
    for (const part of bracket?.parts ?? []) {
        for (const column of part.columns ?? []) {
            if (column.lockPicks) {
                locked.add(`${part.identifier}:${num(column.index)}`);
            }
        }
    }

    const parts =
        (bracket?.freeform ? bracket?.freeformParts : bracket?.parts) ?? [];
    return parts
        .flatMap((part) =>
            (part.columns ?? []).filter(
                (column) =>
                    !locked.has(`${part.identifier}:${num(column.index)}`)
            )
        )
        .reduce((total, column) => total + (column.matches?.length ?? 0), 0);
}

